import { appConfigStore } from 'Models/AppConfigStore';
import {
	canNavigateToCartStep,
	canNavigateToPaymentStep,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { getAddOnsTabVisibility, getExistingAddOnIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import { checkIfInvalidName } from 'Util/StringUtils';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { isEmail } from 'Validators/Functions/Email';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import AddOnsTab from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/AddOn/AddOnTab';
import { BookingWizardTab, SidebarStatus, VisibilityStatus } from '../BookingWizard';
import { BookingWizardTabNames } from '../BookingWizardConstants';
import { BookingWizardData } from '../BookingWizardData/BaseBookingWizardData';
import { FreightBookingWizardData, validateFreightDetail } from '../BookingWizardData/FreightBookingWizardData';
import { WizardErrors } from '../BookingWizardTypes';
import FreightContactTab from './FreightContact/FreightContactTab';
import BulkShipmentTab from './Shipment/BulkShipmentTab';
import ShipmentTab from './Shipment/ShipmentTab';
import { getDisableLinkForMultiFreight } from '../ProgressBar/Utils/getDisableLinkForMultiFreight';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { Location } from 'react-router-dom';
import { useDynamicBookingFieldsStore, getVisibleDynamicFields } from 'Stores/DynamicBookingFieldsStore';
import { validateDynamicFields } from './BookingDetails/DynamicFieldsSection';

export function getFreightBookingTabs(
	wizardData: BookingWizardData,
	setBaseWizardErrors: (errors: WizardErrors<BookingWizardData>) => void,
	setInnerWizardErrors: (errors: WizardErrors<FreightBookingWizardData>) => void,
	location: Location,
	selectedTrips: SelectedTrips,
	freightTypeDtos: FreightTypeDto[],
	oldWizardData?: BookingWizardData | null,
	// The booking subject's customer type (selected customer for staff-on-behalf) —
	// resolved reactively by the caller so the tabs rebuild when it lands.
	subjectUserDisplayNameId: string | null = null,
): BookingWizardTab[] {
	// It is truly a new booking when there is no bookingToEdit id
	const isNewBooking = wizardData.wizardMode === 'CREATE' && !wizardData.bookingToEdit;

	const tabs: BookingWizardTab[] = [
		{
			name: BookingWizardTabNames.Contact,
			displayName: 'Contact',
			path: 'contact',
			tabTitle: 'Contact details',
			tabTitleEdit: 'Edit contact details',
			// Visible when receiver or notify details are enabled, or when at least
			// one dynamic field targets FREIGHT for the booking subject — the contact
			// tab also hosts the freight "Additional information" dynamic fields.
			visibility: (() => {
				if (appConfigStore.freightReceiverDetailsEnabled || appConfigStore.freightNotifyDetailsEnabled) {
					return VisibilityStatus.VISIBLE;
				}
				const fields = useDynamicBookingFieldsStore.getState().fields;
				return getVisibleDynamicFields(fields, 'FREIGHT', subjectUserDisplayNameId).length > 0
					? VisibilityStatus.VISIBLE
					: VisibilityStatus.EXCLUDED;
			})(),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newBaseErrors: WizardErrors<BookingWizardData> = {};
				const newInnerErrors: WizardErrors<FreightBookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
					}
					return true;
				}

				if (wizardData.innerWizardData.type === 'freight') {
					if (appConfigStore.freightReceiverDetailsFirstNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.receiverFirstName) &&
							appConfigStore.freightReceiverDetailsFirstNameRequired
						) {
							newInnerErrors.receiverFirstName = 'Required field';
						} else if (
							checkIfInvalidName(wizardData.innerWizardData.receiverFirstName, true) &&
							stringNotEmpty(wizardData.innerWizardData.receiverFirstName)
						) {
							newInnerErrors.receiverFirstName = "A-Z, spaces and '-' only.";
						}
					}

					if (appConfigStore.freightReceiverDetailsLastNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.receiverLastName) &&
							appConfigStore.freightReceiverDetailsLastNameRequired
						) {
							newInnerErrors.receiverLastName = 'Required field';
						} else if (
							checkIfInvalidName(wizardData.innerWizardData.receiverLastName, true) &&
							stringNotEmpty(wizardData.innerWizardData.receiverLastName)
						) {
							newInnerErrors.receiverLastName = "A-Z, spaces and '-' only.";
						}
					}

					if (appConfigStore.freightReceiverDetailsPhoneEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.receiverPhone) &&
							appConfigStore.freightReceiverDetailsPhoneRequired
						) {
							newInnerErrors.receiverPhone = 'Required field';
						} else if (
							!IsValidPhone(wizardData.innerWizardData.receiverPhone) &&
							stringNotEmpty(wizardData.innerWizardData.receiverPhone)
						) {
							newInnerErrors.receiverPhone = 'Enter a valid phone number';
						}
					}

					if (appConfigStore.freightReceiverDetailsEmailEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.receiverEmail) &&
							appConfigStore.freightReceiverDetailsEmailRequired
						) {
							newInnerErrors.receiverEmail = 'Required field';
						} else if (
							!isEmail(wizardData.innerWizardData.receiverEmail) &&
							stringNotEmpty(wizardData.innerWizardData.receiverEmail)
						) {
							newInnerErrors.receiverEmail = 'Enter a valid email address (xx@xx.xx)';
						}
					}

					if (appConfigStore.freightReceiverDetailsCompanyNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.receiverCompanyName) &&
							appConfigStore.freightReceiverDetailsCompanyNameRequired
						) {
							newInnerErrors.receiverCompanyName = 'Required field';
						}
					}

					if (appConfigStore.freightNotifyDetailsFirstNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.notifyFirstName) &&
							appConfigStore.freightNotifyDetailsFirstNameRequired
						) {
							newInnerErrors.notifyFirstName = 'Required field';
						} else if (
							checkIfInvalidName(wizardData.innerWizardData.notifyFirstName, true) &&
							stringNotEmpty(wizardData.innerWizardData.notifyFirstName)
						) {
							newInnerErrors.notifyFirstName = "A-Z, spaces and '-' only.";
						}
					}

					if (appConfigStore.freightNotifyDetailsLastNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.notifyLastName) &&
							appConfigStore.freightNotifyDetailsLastNameRequired
						) {
							newInnerErrors.notifyLastName = 'Required field';
						} else if (
							checkIfInvalidName(wizardData.innerWizardData.notifyLastName, true) &&
							stringNotEmpty(wizardData.innerWizardData.notifyLastName)
						) {
							newInnerErrors.notifyLastName = "A-Z, spaces and '-' only.";
						}
					}

					if (appConfigStore.freightNotifyDetailsPhoneEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.notifyPhone) &&
							appConfigStore.freightNotifyDetailsPhoneRequired
						) {
							newInnerErrors.notifyPhone = 'Required field';
						} else if (
							!IsValidPhone(wizardData.innerWizardData.notifyPhone) &&
							stringNotEmpty(wizardData.innerWizardData.notifyPhone)
						) {
							newInnerErrors.notifyPhone = 'Enter a valid phone number';
						}
					}

					if (appConfigStore.freightNotifyDetailsEmailEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.notifyEmail) &&
							appConfigStore.freightNotifyDetailsEmailRequired
						) {
							newInnerErrors.notifyEmail = 'Required field';
						} else if (
							!isEmail(wizardData.innerWizardData.notifyEmail) &&
							stringNotEmpty(wizardData.innerWizardData.notifyEmail)
						) {
							newInnerErrors.notifyEmail = 'Enter a valid email address (xx@xx.xx)';
						}
					}

					if (appConfigStore.freightNotifyDetailsCompanyNameEnabled) {
						if (
							stringIsEmpty(wizardData.innerWizardData.notifyCompanyName) &&
							appConfigStore.freightNotifyDetailsCompanyNameRequired
						) {
							newInnerErrors.notifyCompanyName = 'Required field';
						}
					}

					// Admin-configured dynamic fields targeted at FREIGHT bookings.
					const dynamicFields = useDynamicBookingFieldsStore.getState().fields;
					const dynamicErrors = validateDynamicFields(
						dynamicFields,
						'FREIGHT',
						subjectUserDisplayNameId,
						wizardData.innerWizardData.dynamicFieldValues,
					);
					if (Object.keys(dynamicErrors).length > 0) {
						(newInnerErrors as any).dynamicFieldValues = dynamicErrors;
					}

					if (updateErrorState) {
						setInnerWizardErrors(newInnerErrors);
					}
				}
				return Object.keys(newInnerErrors).length === 0 && Object.keys(newBaseErrors).length === 0;
			},
			component: <FreightContactTab />,
			authorize: true,
			showTAndCsButton: false,
			isLinkDisabled: getDisableLinkForMultiFreight(wizardData),
		},
		{
			name: BookingWizardTabNames.Shipment,
			displayName: 'Shipment',
			path: 'shipment',
			tabTitle: 'Shipment',
			tabTitleEdit: 'Edit Shipment',
			visibility: VisibilityStatus.VISIBLE,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newBaseErrors: WizardErrors<BookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
					}
					return true;
				}

				if (wizardData.innerWizardData.type === 'freight') {
					let hasErrors = false;

					if (isNewBooking) {
						for (const item of wizardData.innerWizardData.items) {
							const validateFreightDetailContext = {
								freightTypeDtos,
								freightDimensionsHiddenOnStaticPriceEnabled:
									appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled,
							};
							const itemErrors = validateFreightDetail(item, validateFreightDetailContext);
							item.errors = itemErrors;
							if (Object.keys(itemErrors).length > 0) {
								hasErrors = true;
							}
						}
					} else {
						const validateFreightDetailContext = {
							freightTypeDtos,
							freightDimensionsHiddenOnStaticPriceEnabled:
								appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled,
						};
						const newInnerErrors: WizardErrors<FreightBookingWizardData> = validateFreightDetail(
							wizardData.innerWizardData,
							validateFreightDetailContext,
						);
						if (updateErrorState) {
							setInnerWizardErrors(newInnerErrors);
						}
						hasErrors = Object.keys(newInnerErrors).length > 0 || Object.keys(newBaseErrors).length > 0;
					}

					if (!hasErrors) {
						return true;
					}

					if (isNewBooking && updateErrorState) {
						// Errors for new bookings are stored on item.errors directly, so poke the
						// store to force a re-render. Must NOT run in the edit path — it would wipe
						// the errors set via setInnerWizardErrors above before they ever render.
						setInnerWizardErrors({});
					}
				}
				return false;
			},
			component: isNewBooking ? <BulkShipmentTab selectedTrips={selectedTrips} /> : <ShipmentTab />,
			authorize: true,
			showTAndCsButton: false,
			isLinkDisabled: getDisableLinkForMultiFreight(wizardData),
		},
	];

	const existingFreightAddOnIds = getExistingAddOnIds(wizardData, oldWizardData);

	if (!isNewBooking) {
		tabs.push({
			name: BookingWizardTabNames.AddOns,
			displayName: 'Add ons',
			path: 'add-ons',
			tabTitle: 'Add-ons',
			tabTitleEdit: 'Edit add-ons',
			visibility: getAddOnsTabVisibility(wizardData.tabSelected, selectedTrips, existingFreightAddOnIds),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: () => true,
			component: (
				<>
					<AddOnsTab selectedTrips={selectedTrips} bookingType="freight" />
				</>
			),
			authorize: true,
			showTAndCsButton: false,
		});
	}

	return tabs;
}
